// src/components/ui/ResizablePanel.tsx
// -----------------------------------------------------------------------------
// ResizablePanel - A panel component with drag-to-resize functionality
// -----------------------------------------------------------------------------
// Features:
// - Horizontal resize via drag handle on edge
// - Minimum/maximum width constraints
// - Collapsible with toggle button (shows label when collapsed)
// - Smooth animations
// - Saves user preference to localStorage
// -----------------------------------------------------------------------------

import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  DragHandle,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import logger from "utils/logger";

interface ResizablePanelProps {
  /** Panel content */
  children: React.ReactNode;
  /** Default width in pixels */
  defaultWidth?: number;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Maximum width in pixels */
  maxWidth?: number;
  /** Which side the resize handle is on */
  resizeEdge?: "left" | "right";
  /** Whether the panel is collapsible */
  collapsible?: boolean;
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Storage key for persistence (omit to disable) */
  storageKey?: string;
  /** Called when width changes */
  onWidthChange?: (width: number) => void;
  /** Called when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Disable the collapse toggle (e.g. when the other panel is collapsed) */
  collapseDisabled?: boolean;
  /** Label to display when the panel is collapsed (e.g. "Data", "Chat") */
  collapseLabel?: string;
  /** Additional styles for the panel */
  sx?: object;
}

const ResizablePanel: React.FC<ResizablePanelProps> = ({
  children,
  defaultWidth = 350,
  minWidth = 200,
  maxWidth = 600,
  resizeEdge = "right",
  collapsible = true,
  defaultCollapsed = false,
  storageKey,
  onWidthChange,
  onCollapsedChange,
  collapseDisabled = false,
  collapseLabel,
  sx = {},
}) => {
  const theme = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  const [isResizing, setIsResizing] = useState(false);

  // Load initial state from localStorage
  const loadStoredState = useCallback(() => {
    if (!storageKey) return { width: defaultWidth, collapsed: defaultCollapsed };
    
    try {
      const stored = localStorage.getItem(`resizable-panel-${storageKey}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          width: Math.min(Math.max(parsed.width || defaultWidth, minWidth), maxWidth),
          collapsed: parsed.collapsed ?? defaultCollapsed,
        };
      }
    } catch (e) {
      logger.error("[ResizablePanel] Failed to load stored state:", e);
    }
    return { width: defaultWidth, collapsed: defaultCollapsed };
  }, [storageKey, defaultWidth, defaultCollapsed, minWidth, maxWidth]);

  const [width, setWidth] = useState(() => loadStoredState().width);
  const [collapsed, setCollapsed] = useState(() => loadStoredState().collapsed);

  // Save state to localStorage
  const saveState = useCallback(
    (newWidth: number, newCollapsed: boolean) => {
      if (!storageKey) return;
      try {
        localStorage.setItem(
          `resizable-panel-${storageKey}`,
          JSON.stringify({ width: newWidth, collapsed: newCollapsed })
        );
      } catch (e) {
        logger.error("[ResizablePanel] Failed to save state:", e);
      }
    },
    [storageKey]
  );

  // Handle resize
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = width;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = resizeEdge === "right"
          ? moveEvent.clientX - startX
          : startX - moveEvent.clientX;

        const newWidth = Math.min(Math.max(startWidth + delta, minWidth), maxWidth);
        setWidth(newWidth);
        onWidthChange?.(newWidth);
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        
        // Save final width
        const panel = panelRef.current;
        if (panel) {
          saveState(panel.offsetWidth, collapsed);
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [width, minWidth, maxWidth, resizeEdge, collapsed, onWidthChange, saveState]
  );

  // Handle collapse toggle
  const toggleCollapse = useCallback(() => {
    if (collapseDisabled) return;
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    onCollapsedChange?.(newCollapsed);
    saveState(width, newCollapsed);
  }, [collapsed, width, onCollapsedChange, saveState, collapseDisabled]);

  // Apply cursor style during resize
  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  const ExpandIcon = resizeEdge === "right" ? ChevronRight : ChevronLeft;
  const CollapseIcon = resizeEdge === "right" ? ChevronLeft : ChevronRight;

  return (
    <Box
      ref={panelRef}
      sx={{
        position: "relative",
        width: collapsed ? "48px" : width,
        minWidth: collapsed ? "48px" : minWidth,
        maxWidth: collapsed ? "48px" : maxWidth,
        height: "100%",
        transition: isResizing ? "none" : "width 0.2s ease, min-width 0.2s ease, max-width 0.2s ease",
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      {/* Collapse Toggle — when COLLAPSED: vertical labeled button; when EXPANDED: small corner arrow */}
      {collapsible && collapsed && (
        <Tooltip title={collapseLabel ? `Expand ${collapseLabel.toLowerCase()}` : "Expand panel"}>
          <Box
            onClick={toggleCollapse}
            role="button"
            tabIndex={0}
            sx={{
              position: "absolute",
              top: 8,
              [resizeEdge === "right" ? "right" : "left"]: 4,
              transform: "rotate(-90deg)",
              transformOrigin: resizeEdge === "right" ? "top right" : "top left",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              whiteSpace: "nowrap",
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              zIndex: 10,
              backgroundColor: alpha(
                theme.palette.background.paper,
                theme.custom?.containerOpacity?.heavy || 0.88
              ),
              border: `1px solid ${theme.palette.divider}`,
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ExpandIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 600,
                color: theme.palette.text.secondary,
                letterSpacing: 0.5,
                textTransform: "uppercase",
              }}
            >
              {collapseLabel || "Expand"}
            </Typography>
          </Box>
        </Tooltip>
      )}

      {collapsible && !collapsed && (
        <Tooltip
          title={collapseDisabled ? "Expand other panel first" : "Collapse panel"}
          placement={resizeEdge === "right" ? "left" : "right"}
        >
          <span /* wrapper so Tooltip works on disabled button */>
            <IconButton
              onClick={toggleCollapse}
              disabled={collapseDisabled}
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                [resizeEdge]: 8,
                zIndex: 10,
                backgroundColor: alpha(theme.palette.background.paper, theme.custom?.containerOpacity?.heavy || 0.88),
                border: `1px solid ${theme.palette.divider}`,
                width: 24,
                height: 24,
                "&:hover": {
                  backgroundColor: collapseDisabled ? undefined : theme.palette.action.hover,
                },
                opacity: collapseDisabled ? 0.4 : 1,
              }}
            >
              <CollapseIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Panel Content */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0, // Critical: allows flex child to shrink for scrolling
          overflow: "hidden", // Children handle their own scroll
          opacity: collapsed ? 0 : 1,
          transition: "opacity 0.2s ease",
          visibility: collapsed ? "hidden" : "visible",
        }}
      >
        {children}
      </Box>

      {/* Resize Handle */}
      {!collapsed && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            [resizeEdge]: -4,
            width: 8,
            cursor: "col-resize",
            backgroundColor: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background-color 0.15s ease",
            zIndex: 5,
            "&:hover, &:active": {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 4,
              height: 40,
              borderRadius: 2,
              backgroundColor: isResizing
                ? theme.palette.primary.main
                : alpha(theme.palette.text.secondary, 0.3),
              transition: "background-color 0.15s ease",
            },
            "&:hover::after": {
              backgroundColor: theme.palette.primary.main,
            },
          }}
        >
          <DragHandle
            sx={{
              fontSize: 16,
              color: theme.custom?.contentText?.muted || alpha(theme.palette.text.secondary, 0.5),
              transform: "rotate(90deg)",
              opacity: 0,
              transition: "opacity 0.15s ease",
              ".MuiBox-root:hover &": {
                opacity: 1,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ResizablePanel;
